import type {
  CarePlan,
  Goal,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes';

import {
  type FrequencySegment,
  type LadderVisit,
  activityStickRef,
  diffDays,
  formatSegmentCadence,
  isSpineStick,
  projectVisitLadder,
  sessionFrequencyOf,
  visitOverridesOf,
} from '@eluve/plan-shapes';

/**
 * Page-side derivation of a patient's CarePlans into what the Care Plans
 * section renders (ENG-5369 scope). Pure derivations over the fetched set —
 * no fetching, no React — kept decoupled from presentation per the ticket.
 * Phase/progress semantics come from `@eluve/plan-shapes` (the spine on
 * `CarePlan.activity`, per-phase spine-stick ServiceRequests, the
 * session-frequency extension, the projected visit ladder); nothing is
 * re-encoded here.
 */

export type CarePlanList = {
  /** Plans in flight, newest start first — the cards the section renders. */
  active: CarePlan[];
  /** Proposals awaiting review — surfaced only in the no-active-plan note. */
  drafts: CarePlan[];
};

/** Stable routing key for the expanded card deep link — the resource id. */
export function carePlanToken(plan: CarePlan): string {
  return plan.id ?? 'unknown';
}

function planSortKey(plan: CarePlan): string {
  return plan.period?.start ?? plan.created ?? '';
}

/**
 * Bucket a patient's care plans for the section. Only `active` plans render
 * as cards; `draft` proposals feed the empty-state note. Everything else
 * (completed, revoked, on-hold, entered-in-error, unknown) is history the
 * overview deliberately doesn't show.
 */
export function buildCarePlanList(plans: readonly CarePlan[]): CarePlanList {
  const newestFirst = (a: CarePlan, b: CarePlan) =>
    planSortKey(b).localeCompare(planSortKey(a));
  return {
    active: plans.filter((plan) => plan.status === 'active').sort(newestFirst),
    drafts: plans.filter((plan) => plan.status === 'draft').sort(newestFirst),
  };
}

/** Find a plan by its routing token across both buckets. */
export function findPlanByToken(
  list: CarePlanList,
  token: string | null,
): CarePlan | undefined {
  if (!token) return undefined;
  return [...list.active, ...list.drafts].find(
    (plan) => carePlanToken(plan) === token,
  );
}

/**
 * The plan's spine-stick ServiceRequests in phase order: one slot per
 * CarePlan.activity, resolved through the activity's stick reference
 * (ADR-0016), with unreferenced sticks appended. A slot is `undefined` when
 * the activity has no stick reference or the stick isn't in the loaded
 * window — the hole keeps later phases aligned to their own sticks instead
 * of shifting them left.
 */
export function orderedPhaseSticks(
  plan: CarePlan,
  serviceRequests: readonly ServiceRequest[],
): (ServiceRequest | undefined)[] {
  const planRef = `CarePlan/${plan.id}`;
  const sticksById = new Map(
    serviceRequests.flatMap((sr) =>
      isSpineStick(sr) &&
      sr.id &&
      (sr.basedOn ?? []).some((ref) => ref.reference === planRef)
        ? [[sr.id, sr] as const]
        : [],
    ),
  );
  const ordered: (ServiceRequest | undefined)[] = [];
  for (const activity of plan.activity ?? []) {
    const id = activityStickRef(activity)?.split('/')[1];
    const stick = id ? sticksById.get(id) : undefined;
    ordered.push(stick);
    if (id && stick) {
      sticksById.delete(id);
    }
  }
  ordered.push(...sticksById.values());
  return ordered;
}

export type PhaseState = 'done' | 'current' | 'upcoming';

export type PhaseStep = {
  title: string;
  state: PhaseState;
  /** "Weeks 1–2" against the plan start; absent without a dated schedule. */
  weeksLabel?: string;
};

/** Stick status → stepper state (accept-time choreography, ADR-0016). */
function stickState(status: ServiceRequest['status']): PhaseState {
  if (status === 'completed') return 'done';
  if (status === 'active') return 'current';
  return 'upcoming';
}

/** CarePlan.activity.detail.status → stepper state (stickless fallback). */
function detailState(status: string | undefined): PhaseState {
  if (status === 'completed') return 'done';
  if (status === 'in-progress') return 'current';
  return 'upcoming';
}

function weeksLabel(
  planStart: string | undefined,
  start: string | undefined,
  end: string | undefined,
): string | undefined {
  if (!planStart || !start || !end) return undefined;
  const startWeek = Math.floor(diffDays(planStart, start) / 7) + 1;
  const endWeek = Math.max(startWeek, Math.ceil(diffDays(planStart, end) / 7));
  return startWeek === endWeek
    ? `Week ${startWeek}`
    : `Weeks ${startWeek}–${endWeek}`;
}

/**
 * The phase stepper: one step per CarePlan.activity (the spine, in authored
 * order — same source as `decodeInstanceSpec`), its state from the matching
 * spine stick's searchable status, falling back to the activity detail's
 * status when the stick is missing from the loaded window.
 */
export function planPhaseSteps(
  plan: CarePlan,
  serviceRequests: readonly ServiceRequest[],
): PhaseStep[] {
  const sticks = orderedPhaseSticks(plan, serviceRequests);
  const planStart = plan.period?.start?.slice(0, 10);
  return (plan.activity ?? []).map((activity, index) => {
    const detail = activity.detail;
    const stick = sticks[index];
    return {
      title:
        detail?.code?.text ?? detail?.code?.coding?.[0]?.display ?? 'Phase',
      state: stick ? stickState(stick.status) : detailState(detail?.status),
      weeksLabel: weeksLabel(
        planStart,
        detail?.scheduledPeriod?.start?.slice(0, 10),
        detail?.scheduledPeriod?.end?.slice(0, 10),
      ),
    };
  });
}

/** The in-flight phase the collapsed card names ("Now: …"). */
export function currentPhase(steps: readonly PhaseStep[]): PhaseStep | null {
  return steps.find((step) => step.state === 'current') ?? null;
}

export type PlanProgress = {
  weekNow: number;
  weekTotal: number;
  /** 0–100, elapsed time against the dated span. */
  percent: number;
};

/** The plan's estimated end: its period end, else the last dated phase's. */
export function planEndDate(plan: CarePlan): string | undefined {
  if (plan.period?.end) return plan.period.end.slice(0, 10);
  const ends = (plan.activity ?? [])
    .map((activity) => activity.detail?.scheduledPeriod?.end?.slice(0, 10))
    .filter((end): end is string => Boolean(end));
  return ends.length > 0 ? ends[ends.length - 1] : undefined;
}

/**
 * "Week N of M" + the progress-bar fill, from elapsed time against the
 * plan's dated span. Null without both a start and a (derived) end — the
 * card degrades to phases-only.
 */
export function planProgress(
  plan: CarePlan,
  todayIsoDate: string,
): PlanProgress | null {
  const start = plan.period?.start?.slice(0, 10);
  const end = planEndDate(plan);
  if (!start || !end) return null;
  const totalDays = diffDays(start, end);
  if (totalDays <= 0) return null;
  const weekTotal = Math.max(1, Math.ceil(totalDays / 7));
  const elapsed = Math.min(
    Math.max(diffDays(start, todayIsoDate), 0),
    totalDays,
  );
  const weekNow = Math.min(weekTotal, Math.floor(elapsed / 7) + 1);
  return {
    weekNow,
    weekTotal,
    percent: Math.round((elapsed / totalDays) * 1000) / 10,
  };
}

export type TreatedCondition = {
  /** Routing token for the Conditions drawer (`?condition=<id>`). */
  token: string;
  display: string;
};

function referenceId(reference: Reference): string | undefined {
  return reference.reference?.split('/')[1];
}

/** The Conditions the plan treats (`CarePlan.addresses`), drawer-linkable. */
export function treatedConditions(plan: CarePlan): TreatedCondition[] {
  return (plan.addresses ?? [])
    .filter((ref) => ref.reference?.startsWith('Condition/'))
    .flatMap((ref) => {
      const id = referenceId(ref);
      return id ? [{ token: id, display: ref.display ?? 'Condition' }] : [];
    });
}

export type LinkedGoal = {
  /** Routing token for the Goals drawer (`?goal=<id>`). */
  token: string;
  display: string;
  /** The resolved resource when it's in the loaded goal window. */
  goal?: Goal;
};

/**
 * The plan's goals resolved against the loaded goal window, in plan order.
 * A goal outside the window (or retracted since linking) degrades to its
 * reference display — the row stays linkable either way.
 */
export function linkedGoals(
  plan: CarePlan,
  goals: readonly Goal[],
): LinkedGoal[] {
  const byId = new Map(
    goals.flatMap((goal) => (goal.id ? [[goal.id, goal] as const] : [])),
  );
  return (plan.goal ?? [])
    .filter((ref) => ref.reference?.startsWith('Goal/'))
    .flatMap((ref) => {
      const id = referenceId(ref);
      if (!id) return [];
      const goal = byId.get(id);
      const display =
        goal?.description?.text ??
        goal?.description?.coding?.[0]?.display ??
        ref.display ??
        'Goal';
      return [{ token: id, display, ...(goal ? { goal } : {}) }];
    });
}

export type CadenceRow = {
  /** "Weeks 1–2", "Weeks 9–13", or "Ongoing" for an open-ended tail. */
  weeksLabel: string;
  /** "2×/wk" — plan-shapes' canonical cadence formatting. */
  cadence: string;
};

const SEGMENT_UNIT_DAYS = { d: 1, wk: 7, mo: 30 } as const;

function segmentDays(segment: FrequencySegment): number | null {
  if (segment.duration === undefined || !segment.durationUnit) return null;
  return segment.duration * SEGMENT_UNIT_DAYS[segment.durationUnit];
}

/**
 * The cadence-by-phase table from the plan's session-frequency snapshot:
 * each segment labelled by the cumulative week range it spans. Null when
 * the plan carries no cadence extension.
 */
export function cadenceRows(plan: CarePlan): CadenceRow[] | null {
  const segments = sessionFrequencyOf(plan);
  if (!segments) return null;
  const rows: CadenceRow[] = [];
  let dayCursor = 0;
  for (const segment of segments) {
    const days = segmentDays(segment);
    const cadence = formatSegmentCadence(segment);
    if (days === null) {
      rows.push({ weeksLabel: 'Ongoing', cadence });
      break;
    }
    const startWeek = Math.floor(dayCursor / 7) + 1;
    dayCursor += days;
    const endWeek = Math.max(startWeek, Math.ceil(dayCursor / 7));
    rows.push({
      weeksLabel:
        startWeek === endWeek
          ? `Week ${startWeek}`
          : `Weeks ${startWeek}–${endWeek}`,
      cadence,
    });
  }
  return rows.length > 0 ? rows : null;
}

/**
 * The projected visit schedule (plan-shapes' ladder, run without linked
 * encounters/appointments — encounters-on-plan is out of scope here, so
 * every visit projects as upcoming). Projections dated before today are
 * dropped: without encounters they are neither past visits nor upcoming
 * ones, just stale math. Null without a cadence, a start date, or any
 * remaining visit.
 */
export function projectedVisits(
  plan: CarePlan,
  serviceRequests: readonly ServiceRequest[],
  todayIsoDate: string,
): LadderVisit[] | null {
  const segments = sessionFrequencyOf(plan);
  const startDate = plan.period?.start?.slice(0, 10);
  if (!segments || !startDate) return null;
  const ladder = projectVisitLadder({
    segments,
    startDate,
    overrides: visitOverridesOf(plan),
    phaseVisitTargets: orderedPhaseSticks(plan, serviceRequests).map(
      (stick) => stick?.quantityQuantity?.value,
    ),
  });
  const upcoming = ladder.visits.filter(
    (visit) => visit.date !== undefined && visit.date >= todayIsoDate,
  );
  return upcoming.length > 0 ? upcoming : null;
}
